import { z } from 'zod';

import { chargePaymentMethodSchema } from './charge.schema';
import { billingCycleSchema } from './plan.schema';
import { todayLocal } from '../utils/datetime';

// =============================================================================
//  Subscription schemas — Story 3.1 (Base CRUD)
//
//  Escopo nesta story: SÓ owl_managed.
//  - saas_direct é E8 (sync hooks).
//  - migration é caso especial (não exposta via API).
//  - Wizard 3 passos é 3.2.
//  - Cancelamento/upgrade/downgrade/transfer/discount/trial são 3.3-3.9.
//
//  PATCH em 3.1 SÓ aceita campos não-críticos (observations, auto_renew, sold_by_user_id, sale_origin).
// =============================================================================

export const subscriptionStatusSchema = z.enum([
  'ativa',
  'em_teste',
  'trial_expired',
  'aguardando_pagamento',
  'inadimplente',
  'suspensa',
  'pending_cancellation',
  'cancelada',
  'pending_provisioning',
  'encerrada',
]);
export type SubscriptionStatusSchema = z.infer<typeof subscriptionStatusSchema>;

export const subscriptionOriginSchema = z.enum(['owl_managed', 'saas_direct', 'migration']);
export type SubscriptionOriginSchema = z.infer<typeof subscriptionOriginSchema>;

export const paymentMethodSchema = z.enum([
  'pix',
  'boleto',
  'cartao',
  'transferencia',
  'cobranca_manual',
]);
export type PaymentMethodSchema = z.infer<typeof paymentMethodSchema>;

export const saleOriginSchema = z.enum([
  'inbound',
  'outbound',
  'referral',
  'self_service',
  'migration',
]);
export type SaleOriginSchema = z.infer<typeof saleOriginSchema>;

// -----------------------------------------------------------------------------
// CREATE — owl_managed only
// -----------------------------------------------------------------------------

export const createSubscriptionSchema = z.object({
  client_id: z.string().uuid('client_id inválido.'),
  plan_id: z.string().uuid('plan_id inválido.'),
  start_date: z.coerce.date({ invalid_type_error: 'start_date inválido.' }),
  billing_cycle: billingCycleSchema,
  payment_method: paymentMethodSchema.optional().nullable(),
  auto_renew: z.coerce.boolean().default(true),
  observations: z.string().max(5000, 'Observações até 5000 caracteres.').optional().nullable(),
  sold_by_user_id: z.string().uuid().optional().nullable(),
  sale_origin: saleOriginSchema.optional().nullable(),
  /** Story 3.9 AC4 — admin força criação de trial mesmo se cliente já usou trial deste plano. */
  force_trial: z.coerce.boolean().optional(),
});
export type CreateSubscriptionDto = z.output<typeof createSubscriptionSchema>;
export type CreateSubscriptionInput = z.input<typeof createSubscriptionSchema>;

// -----------------------------------------------------------------------------
// CREATE STANDALONE — Story 4.13: cobrança avulsa RECORRENTE (sem plano)
//
// Cria uma Subscription standalone: planId=null, price=valor livre digitado,
// billingCycle escolhido, description livre. Sem entitlements / sem
// provisionamento. Reusa o motor de recorrência (cron 4.3). Difere de
// createSubscriptionSchema por NÃO ter plan_id e por trazer amount/description
// próprios. Convenção camelCase pra casar com a tela de charges (a UI vive lá).
// -----------------------------------------------------------------------------

/** Ciclos válidos pra recorrente: todos menos `pagamento_unico` (esse é a avulsa única). */
export const recurringBillingCycleSchema = billingCycleSchema.exclude(['pagamento_unico']);
export type RecurringBillingCycle = z.infer<typeof recurringBillingCycleSchema>;

export const createStandaloneSubscriptionSchema = z.object({
  clientId: z.string().uuid('clientId inválido.'),
  amount: z
    .number()
    .positive('Valor deve ser maior que zero')
    .max(999_999.99, 'Valor excede limite máximo')
    .multipleOf(0.01, 'Valor deve ter no máximo 2 casas decimais'),
  description: z
    .string()
    .trim()
    .min(1, 'Descrição obrigatória')
    .max(200, 'Descrição deve ter no máximo 200 caracteres'),
  billingCycle: recurringBillingCycleSchema,
  // Story 4.14 (D1): método escolhido pelo CLIENTE no checkout da 1ª cobrança.
  // Opcional na criação — a 1ª charge nasce em aguardando_pagamento, sem método.
  paymentMethod: chargePaymentMethodSchema.optional(),
  startDate: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Data deve estar no formato YYYY-MM-DD')
    .refine((v) => v >= todayLocal(), 'Início deve ser hoje ou no futuro'),
});
export type CreateStandaloneSubscriptionDto = z.infer<typeof createStandaloneSubscriptionSchema>;

// -----------------------------------------------------------------------------
// UPDATE — APENAS campos não-críticos. Demais ações em stories 3.x.
// -----------------------------------------------------------------------------

export const updateSubscriptionSchema = z
  .object({
    observations: z.string().max(5000).optional().nullable(),
    auto_renew: z.coerce.boolean().optional(),
    sold_by_user_id: z.string().uuid().optional().nullable(),
    sale_origin: saleOriginSchema.optional().nullable(),
    // Story 3.10 — billing_day editável (1-28); só vale na próxima renovação,
    // não altera next_billing_date atual.
    billing_day: z.coerce
      .number()
      .int('billing_day deve ser inteiro.')
      .min(1, 'billing_day deve estar entre 1 e 28.')
      .max(28, 'billing_day deve estar entre 1 e 28.')
      .optional(),
  })
  .strict();
export type UpdateSubscriptionDto = z.output<typeof updateSubscriptionSchema>;
export type UpdateSubscriptionInput = z.input<typeof updateSubscriptionSchema>;

// -----------------------------------------------------------------------------
// CANCEL — Story 3.6
// -----------------------------------------------------------------------------

export const cancelSubscriptionSchema = z.object({
  reason: z.string().min(1, 'Motivo obrigatório.').max(500, 'Motivo deve ter até 500 caracteres.'),
  immediate: z.coerce.boolean().default(false),
});
export type CancelSubscriptionDto = z.output<typeof cancelSubscriptionSchema>;
export type CancelSubscriptionInput = z.input<typeof cancelSubscriptionSchema>;

// -----------------------------------------------------------------------------
// REACTIVATE — Story 3.6
// -----------------------------------------------------------------------------

export const reactivateSubscriptionSchema = z.object({
  reason: z.string().max(500, 'Motivo deve ter até 500 caracteres.').optional().nullable(),
});
export type ReactivateSubscriptionDto = z.output<typeof reactivateSubscriptionSchema>;
export type ReactivateSubscriptionInput = z.input<typeof reactivateSubscriptionSchema>;

// -----------------------------------------------------------------------------
// SUSPEND — Story 3.3
// -----------------------------------------------------------------------------

export const suspendSubscriptionSchema = z.object({
  reason: z.string().min(1, 'Motivo obrigatório.').max(500, 'Motivo deve ter até 500 caracteres.'),
});
export type SuspendSubscriptionDto = z.output<typeof suspendSubscriptionSchema>;
export type SuspendSubscriptionInput = z.input<typeof suspendSubscriptionSchema>;

// -----------------------------------------------------------------------------
// QUERY
// -----------------------------------------------------------------------------

export const querySubscriptionsSchema = z.object({
  client_id: z.string().uuid().optional(),
  plan_id: z.string().uuid().optional(),
  status: subscriptionStatusSchema.optional(),
  origin: subscriptionOriginSchema.optional(),
  cursor: z.string().uuid().optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50),
});
export type QuerySubscriptionsDto = z.infer<typeof querySubscriptionsSchema>;

// ============================================================================
// Story 3.4 — Upgrade (DR-021)
// ============================================================================

/**
 * Body do `POST /api/subscriptions/:id/upgrade` e do preview equivalente.
 * Apenas o id do plano alvo — toda a regra é no service (validação status,
 * preço maior, cálculo proration).
 */
export const upgradeSubscriptionSchema = z.object({
  to_plan_id: z.string().uuid('to_plan_id deve ser UUID válido'),
});
export type UpgradeSubscriptionDto = z.infer<typeof upgradeSubscriptionSchema>;

/** Preview retorna a proration calculada antes da confirmação. */
export const upgradePreviewResponseSchema = z.object({
  data: z.object({
    fromPlanId: z.string().uuid(),
    fromPlanName: z.string(),
    fromPrice: z.string(),
    toPlanId: z.string().uuid(),
    toPlanName: z.string(),
    toPrice: z.string(),
    daysRemaining: z.number().int().nonnegative(),
    cycleDays: z.number().int().positive(),
    prorationAmount: z.string(),
    /** True quando sub está em trial — proration vai a zero. */
    isDuringTrial: z.boolean(),
    nextBillingDate: z.string(),
  }),
});
export type UpgradePreviewResponse = z.infer<typeof upgradePreviewResponseSchema>;

export const upgradeSubscriptionResponseSchema = z.object({
  data: z.object({
    subscriptionId: z.string().uuid(),
    planChangeId: z.string().uuid(),
    prorationChargeId: z.string().uuid().nullable(),
    prorationAmount: z.string(),
    newPlanId: z.string().uuid(),
    newPrice: z.string(),
    entitlementsProvisioned: z.number().int().nonnegative(),
    entitlementsRevokeScheduled: z.number().int().nonnegative(),
  }),
});
export type UpgradeSubscriptionResponse = z.infer<typeof upgradeSubscriptionResponseSchema>;

// ============================================================================
// Story 3.5 — Downgrade (DR-021/2.2 — fim do ciclo, sem reembolso)
// ============================================================================

/**
 * Body do `POST /api/subscriptions/:id/downgrade` e do preview equivalente.
 * Apenas o id do plano alvo — regras (preço menor, mesmo tipo, status válido)
 * validadas no service.
 */
export const downgradeSubscriptionSchema = z.object({
  to_plan_id: z.string().uuid('to_plan_id deve ser UUID válido'),
});
export type DowngradeSubscriptionDto = z.infer<typeof downgradeSubscriptionSchema>;

/** Preview retorna o agendamento sem aplicar mudança. */
export const downgradePreviewResponseSchema = z.object({
  data: z.object({
    fromPlanId: z.string().uuid(),
    fromPlanName: z.string(),
    fromPrice: z.string(),
    toPlanId: z.string().uuid(),
    toPlanName: z.string(),
    toPrice: z.string(),
    /** Data agendada (YYYY-MM-DD) — sempre = nextBillingDate. */
    scheduledFor: z.string(),
    daysRemaining: z.number().int().nonnegative(),
    /** Economia mensal a partir do scheduledFor (fromPrice - toPrice). */
    monthlySavings: z.string(),
  }),
});
export type DowngradePreviewResponse = z.infer<typeof downgradePreviewResponseSchema>;

export const downgradeSubscriptionResponseSchema = z.object({
  data: z.object({
    subscriptionId: z.string().uuid(),
    toPlanId: z.string().uuid(),
    scheduledFor: z.string(),
  }),
});
export type DowngradeSubscriptionResponse = z.infer<typeof downgradeSubscriptionResponseSchema>;

/** Shape do JSON salvo em `subscription.pending_plan_change` — Story 3.5/3.7. */
export const pendingPlanChangeSchema = z.object({
  targetPlanId: z.string().uuid(),
  /**
   * `courtesy_end` (2026-08-13): fim de um upgrade de CORTESIA — o cliente foi
   * subido de plano sem pagar nada e volta ao plano original na data. Difere de
   * `downgrade` em duas coisas que o applier precisa saber: a volta usa
   * `restorePrice` (o preço congelado do cliente, não o de tabela) e acontece
   * MESMO com a assinatura suspensa — senão o brinde vira plano caro de graça
   * pra sempre.
   */
  changeType: z.enum(['downgrade', 'migration', 'courtesy_end']),
  /**
   * Preço a devolver quando `changeType === 'courtesy_end'`. É o `price`
   * congelado da assinatura no momento em que a cortesia foi concedida — pode
   * divergir do `monthlyPrice` de tabela (cliente grandfathered, promocional).
   * Decimal em string pra não perder precisão no JSON.
   */
  restorePrice: z.string().optional(),
  /** YYYY-MM-DD — fim do ciclo corrente quando muda. */
  scheduledFor: z.string(),
  /** ISO 8601 — quando foi agendado. */
  scheduledAt: z.string(),
  /** Usuário que agendou. Null SOMENTE quando `source === 'self_service'`. */
  scheduledByUserId: z.string().uuid().nullable(),
  /**
   * Quem pediu a mudança. `self_service` = o próprio cliente, pelo checkout de
   * troca (não existe admin por trás, então `scheduledByUserId` é null).
   * Ausente = agendamento antigo, feito por admin (compatibilidade).
   */
  source: z.enum(['admin', 'self_service']).optional(),
});
export type PendingPlanChange = z.infer<typeof pendingPlanChangeSchema>;

/**
 * Body do `POST /api/subscriptions/:id/courtesy-upgrade`.
 * Só o plano alvo — toda a regra (elegibilidade, mapeamento no SaaS, janela)
 * é do service, igual às rotas irmãs de upgrade/downgrade.
 */
export const courtesyUpgradeSchema = z.object({
  to_plan_id: z.string().uuid('to_plan_id deve ser UUID válido'),
});
export type CourtesyUpgradeDto = z.infer<typeof courtesyUpgradeSchema>;

export const courtesyUpgradeResponseSchema = z.object({
  data: z.object({
    subscriptionId: z.string().uuid(),
    fromPlanId: z.string().uuid(),
    toPlanId: z.string().uuid(),
    /** YYYY-MM-DD — quando a cortesia acaba e o plano original volta. */
    scheduledFor: z.string(),
    /** Preço congelado que será devolvido na volta. */
    restorePrice: z.string(),
  }),
});
export type CourtesyUpgradeResponse = z.infer<typeof courtesyUpgradeResponseSchema>;

export const cancelPendingDowngradeResponseSchema = z.object({
  data: z.object({
    subscriptionId: z.string().uuid(),
    cancelled: z.literal(true),
  }),
});
export type CancelPendingDowngradeResponse = z.infer<typeof cancelPendingDowngradeResponseSchema>;

// ============================================================================
// Story 3.7 — Transfer (DR-027/8.2 — cessão de contrato)
// ============================================================================

/**
 * Body do `POST /api/subscriptions/:id/transfer`.
 * - `to_client_id`: cliente destino
 * - `reason`: motivo obrigatório (audit trail + AC5)
 */
export const transferSubscriptionSchema = z.object({
  to_client_id: z.string().uuid('to_client_id deve ser UUID válido'),
  reason: z
    .string()
    .trim()
    .min(5, 'Motivo deve ter no mínimo 5 caracteres')
    .max(500, 'Motivo deve ter no máximo 500 caracteres'),
});
export type TransferSubscriptionDto = z.infer<typeof transferSubscriptionSchema>;

export const transferSubscriptionResponseSchema = z.object({
  data: z.object({
    subscriptionId: z.string().uuid(),
    transferId: z.string().uuid(),
    fromClientId: z.string().uuid(),
    toClientId: z.string().uuid(),
    transferredAt: z.string(),
  }),
});
export type TransferSubscriptionResponse = z.infer<typeof transferSubscriptionResponseSchema>;

// ============================================================================
// Story 3.8 — Discount (DR-024 — desconto manual)
// ============================================================================

export const discountTypeSchema = z.enum(['percent', 'fixed']);
export type DiscountTypeSchema = z.infer<typeof discountTypeSchema>;

/**
 * Body do `POST /api/subscriptions/:id/discount`.
 * - `discount_amount > 0`. Se `percent`, schema valida ≤ 100; se `fixed`,
 *   service compara com subscription.price.
 * - `discount_reason` opcional (max 500 chars).
 * - `discount_until` opcional — YYYY-MM-DD > today (sem until = perpétuo).
 */
export const applyDiscountSchema = z
  .object({
    discount_amount: z
      .number()
      .positive('Valor deve ser maior que zero')
      .max(999_999.99, 'Valor excede limite máximo')
      .multipleOf(0.01, 'Valor deve ter no máximo 2 casas decimais'),
    discount_type: discountTypeSchema,
    discount_reason: z
      .string()
      .trim()
      .max(500, 'Motivo deve ter no máximo 500 caracteres')
      .optional(),
    discount_until: z
      .string()
      .regex(/^\d{4}-\d{2}-\d{2}$/, 'Data deve estar no formato YYYY-MM-DD')
      .refine((v) => v > todayLocal(), 'Validade deve ser depois de hoje')
      .optional(),
  })
  .superRefine((data, ctx) => {
    if (data.discount_type === 'percent' && data.discount_amount > 100) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['discount_amount'],
        message: 'Desconto em % não pode passar de 100',
      });
    }
  });
export type ApplyDiscountDto = z.infer<typeof applyDiscountSchema>;

export const applyDiscountResponseSchema = z.object({
  data: z.object({
    subscriptionId: z.string().uuid(),
    discountAmount: z.string(),
    discountType: discountTypeSchema,
    discountReason: z.string().nullable(),
    discountUntil: z.string().nullable(),
    discountAppliedAt: z.string(),
    /** Preço efetivo após desconto. */
    effectivePrice: z.string(),
  }),
});
export type ApplyDiscountResponse = z.infer<typeof applyDiscountResponseSchema>;

export const removeDiscountResponseSchema = z.object({
  data: z.object({
    subscriptionId: z.string().uuid(),
    removed: z.literal(true),
  }),
});
export type RemoveDiscountResponse = z.infer<typeof removeDiscountResponseSchema>;

// ============================================================================
// Story 3.9 — Trial extension (DR-025)
// ============================================================================

/**
 * Body do `POST /api/subscriptions/:id/extend-trial`.
 * - `new_trial_ends_at`: YYYY-MM-DD > today (service compara com trial_ends_at atual)
 * - `reason`: motivo obrigatório (audit trail)
 */
export const extendTrialSchema = z.object({
  new_trial_ends_at: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Data deve estar no formato YYYY-MM-DD')
    .refine((v) => v > todayLocal(), 'Nova data de fim do trial deve ser depois de hoje'),
  reason: z
    .string()
    .trim()
    .min(5, 'Motivo deve ter no mínimo 5 caracteres')
    .max(500, 'Motivo deve ter no máximo 500 caracteres'),
});
export type ExtendTrialDto = z.infer<typeof extendTrialSchema>;

export const extendTrialResponseSchema = z.object({
  data: z.object({
    subscriptionId: z.string().uuid(),
    trialEndsAt: z.string(),
    trialExtendedCount: z.number().int().nonnegative(),
    extendedAt: z.string(),
  }),
});
export type ExtendTrialResponse = z.infer<typeof extendTrialResponseSchema>;
